/**
 * Route metadata registry — the single source of truth for page titles.
 * Consumed by RouteAnnouncer (sets document.title, announces via aria-live).
 *
 * The `breadcrumbs` field is retained for future use (e.g. per-page inline
 * breadcrumbs) but the global breadcrumb slot was removed with the TopBar.
 *
 * ponytail: keep this in sync with the <Route> declarations in App.tsx.
 */

export interface RouteMeta {
  /** react-router path pattern, e.g. "/network/:networkId/quality" */
  pattern: string;
  /** Human-readable page title used for <title> and screen-reader announcement */
  title: (params: Record<string, string | undefined>) => string;
  /** Optional breadcrumb segments; last item marks the current page. Currently unused
   *  by the shell but consumable by any page that wants inline breadcrumbs. */
  breadcrumbs?: (params: Record<string, string | undefined>) => Array<{ label: string; path?: string }>;
}

export const ROUTE_META: RouteMeta[] = [
  {
    pattern: '/',
    title: () => 'Station Explorer',
  },
  {
    pattern: '/search/:method',
    title: (params) => {
      const m = params.method;
      if (m === 'coordinates') return 'Location Search · Station Explorer';
      if (m === 'country') return 'Country Search · Station Explorer';
      return 'Station Search · Station Explorer';
    },
    breadcrumbs: (params) => [
      { label: 'Search', path: '/search/station' },
      {
        label:
          params.method === 'coordinates'
            ? 'By Coordinates'
            : params.method === 'country'
            ? 'By Country'
            : 'By Station',
      },
    ],
  },
  {
    pattern: '/observations',
    title: () => 'Observation Map · Station Explorer',
  },
  {
    pattern: '/observations/:stationCode',
    title: (params) => `Observations — ${params.stationCode ?? 'Station'} · Station Explorer`,
    breadcrumbs: (params) => [
      { label: 'Observations', path: '/observations' },
      { label: params.stationCode ?? 'Station' },
    ],
  },
  {
    pattern: '/availability',
    title: () => 'Data Availability · Station Explorer',
  },
  {
    pattern: '/statistics',
    title: () => 'Station Overview · Station Explorer',
  },
  {
    pattern: '/network',
    title: () => 'Network Health · Station Explorer',
  },
  {
    pattern: '/network/:networkId/quality',
    title: (params) => `${params.networkId ?? 'Network'} Quality · Station Explorer`,
    breadcrumbs: (params) => [
      { label: 'Networks', path: '/network' },
      { label: params.networkId ?? 'Network', path: `/network/${params.networkId}/quality` },
      { label: 'Quality' },
    ],
  },
  {
    pattern: '/network/:networkId/quality/:stationCode',
    title: (params) =>
      `${params.stationCode ?? 'Station'} Sensors — ${params.networkId ?? 'Network'} · Station Explorer`,
    breadcrumbs: (params) => [
      { label: 'Networks', path: '/network' },
      { label: params.networkId ?? 'Network', path: `/network/${params.networkId}/quality` },
      { label: params.stationCode ?? 'Station' },
    ],
  },
  {
    pattern: '/station-quality',
    title: () => 'Station Quality · Station Explorer',
  },
  {
    pattern: '/station',
    title: () => 'Stations · Station Explorer',
    breadcrumbs: () => [{ label: 'Stations' }],
  },
  {
    pattern: '/station/:stationCode',
    title: (params) => `${params.stationCode ?? 'Station'} · Station Explorer`,
    breadcrumbs: (params) => [
      { label: 'Stations', path: '/station' },
      { label: params.stationCode ?? 'Station' },
    ],
  },
  // Rank 9: consolidated /quality routes (replaces /station-quality, /network/:id/quality, /network/:id/quality/:code)
  {
    pattern: '/quality',
    title: () => 'Data Quality · Station Explorer',
    breadcrumbs: () => [{ label: 'Quality' }],
  },
  {
    pattern: '/quality/network/:networkId',
    title: (params) => `${params.networkId ?? 'Network'} Quality · Station Explorer`,
    breadcrumbs: (params) => [
      { label: 'Quality', path: '/quality' },
      { label: params.networkId ?? 'Network' },
    ],
  },
  // /quality/network/:networkId/station/:stationCode retired — sensor quality is
  // now an inline per-network dropdown on the station page (that URL redirects).
  // Rank 9: consolidated station observations route
  {
    pattern: '/station/:stationCode/observations',
    title: (params) => `Observations — ${params.stationCode ?? 'Station'} · Station Explorer`,
    breadcrumbs: (params) => [
      { label: params.stationCode ?? 'Station', path: `/station/${params.stationCode}` },
      { label: 'Observations' },
    ],
  },
  // Rank 9: statistics-under-network tab route
  {
    pattern: '/network/statistics',
    title: () => 'Station Overview · Station Explorer',
    breadcrumbs: () => [
      { label: 'Networks', path: '/network' },
      { label: 'Station Overview' },
    ],
  },
  {
    pattern: '/network/analytics',
    title: () => 'Station Analytics · Station Explorer',
    breadcrumbs: () => [
      { label: 'Networks', path: '/network' },
      { label: 'Station Analytics' },
    ],
  },
];

/** Match a pathname against a react-router-style pattern, extracting params. */
function matchPattern(pattern: string, pathname: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i++) {
    const p = patternParts[i];
    const v = pathParts[i];
    if (p.startsWith(':')) {
      params[p.slice(1)] = decodeURIComponent(v);
    } else if (p !== v) {
      return null;
    }
  }
  return params;
}

/** Resolve route metadata for a given pathname. Returns null if no route matches. */
export function resolveRoute(pathname: string): { meta: RouteMeta; params: Record<string, string> } | null {
  for (const meta of ROUTE_META) {
    const params = matchPattern(meta.pattern, pathname);
    if (params) return { meta, params };
  }
  return null;
}
